/**
 * Data Transformations Module
 *
 * This module contains utilities for transforming and processing data including:
 * - Grouping template payloads by entity (teams, players, tournament)
 * - Transforming thumbnail JSON with custom text parsing
 * - Parsing datafeed metadata
 * - Player and team index/type extraction
 * - Data merging and capture term generation
 */

import { PLAYER_GRAPHICS, TEAM_GRAPHICS } from "constants/phase";
import { ELEMENT_TYPE, TOURNAMENT_KEYS } from "constants/ThumbnailEditor";

import { parseStyledHandlebarText } from "./text-operations";

/**
 * Groups template payloads from thumbnail data by entity type
 * Organizes template data into teams, players, and tournament groups
 *
 * @param {Object} thumbnailData - The thumbnail JSON data containing objects with template payloads
 * @returns {Object} Grouped template payloads organized by entity
 */
export const groupTemplatePayloads = (thumbnailData) => {
  const team_1 = [];
  const team_2 = [];
  const player_1 = [];
  const player_2 = [];
  const player_3 = [];
  const player_4 = [];
  const tournament = [];

  const textMappings = [];

  const objects = thumbnailData?.objects?.filter((item) => item?.templatePayload);

  // Process each payload item
  objects.forEach((obj) => {
    let templatePayload = {
      ...obj?.templatePayload,
      value: obj?.src || obj?.text,
    };
    const { key } = templatePayload;

    // Skip free_text and custom_image
    if (key === "free_text" || key === ELEMENT_TYPE.CUSTOM_IMAGE || key === ELEMENT_TYPE.CAPTURE_IMAGE) {
      return;
    }

    // Handle tournament-related items
    if (TOURNAMENT_KEYS.includes(key)) {
      tournament.push(templatePayload);

      return;
    }

    // Handle team-related items
    if (key.startsWith("team_1_")) {
      team_1.push(templatePayload);

      return;
    }

    if (key.startsWith("team_2_")) {
      team_2.push(templatePayload);

      return;
    }

    // Handle player-related items
    if (key.startsWith("player_1_") || key === "player_1_name") {
      player_1.push(templatePayload);

      return;
    }

    if (key.startsWith("player_2_") || key === "player_2_name") {
      player_2.push(templatePayload);

      return;
    }

    if (key.startsWith("player_3_") || key === "player_3_name") {
      player_3.push(templatePayload);

      return;
    }

    if (key.startsWith("player_4_") || key === "player_4_name") {
      player_4.push(templatePayload);

      return;
    }

    if (key === "custom_text") {
      const templatePayloadRanges = obj?.templatePayloadRanges || [];
      templatePayloadRanges.forEach((range) => {
        const { templatePayload } = range;
        const { key } = templatePayload;
        if (key === "free_text") {
          return;
        }
        if (key.startsWith("player_1_") || key === "player_1_name") {
          player_1.push(templatePayload);

          return;
        }

        if (key.startsWith("player_2_") || key === "player_2_name") {
          player_2.push(templatePayload);

          return;
        }

        if (key.startsWith("player_3_") || key === "player_3_name") {
          player_3.push(templatePayload);

          return;
        }

        if (key.startsWith("player_4_") || key === "player_4_name") {
          player_4.push(templatePayload);

          return;
        }

        if (key.startsWith("team_1_")) {
          team_1.push(templatePayload);

          return;
        }

        if (key.startsWith("team_2_")) {
          team_2.push(templatePayload);

          return;
        }

        textMappings.push(templatePayload);
      });
    }
  });

  return {
    team_1,
    team_2,
    player_1,
    player_2,
    player_3,
    player_4,
    tournament,
    textMappings,
  };
};

/**
 * Transforms thumbnail JSON by parsing custom text fields and extracting template payload ranges
 * Processes textbox elements with custom_text templatePayload and adds templatePayloadRanges
 *
 * @param {Object|string} thumbnailJson - The thumbnail JSON data (object or string)
 * @returns {Object} Transformed thumbnail JSON with parsed template payload ranges
 */
export const transformThumbnailJson = (thumbnailJson) => {
  let parsedJson = thumbnailJson;
  if (typeof thumbnailJson === "string") {
    try {
      parsedJson = JSON.parse(thumbnailJson);
    } catch (error) {
      console.error("Error parsing thumbnail JSON:", error);

      return thumbnailJson;
    }
  }

  const processObjects = (objects = []) => {
    objects.forEach((object) => {
      if (object.type === "textbox" && object?.templatePayload?.key === "custom_text") {
        const { templatePayloadRanges } = parseStyledHandlebarText(object.text);
        object.templatePayload = {
          ...object.templatePayload,
          value: object.text,
        };
        object.templatePayloadRanges = templatePayloadRanges;
      }

      if (object.objects?.length) {
        processObjects(object.objects || object._objects || []);
      }
    });
  };

  processObjects(parsedJson?.objects || parsedJson._objects || []);

  return parsedJson;
};

/**
 * Parses datafeed metadata to extract player name mappings
 * Maps player identifiers (player_1, player_2, etc.) to their full names
 *
 * @param {Array} teamInfo - Array of team/player data objects
 * @returns {Object} Player name mappings (e.g., { player_1: "John Doe", player_2: "Jane Smith" })
 */
export const parseDatafeedMetadataPlayerNames = (teamInfo = []) => {
  const playerNameMappings = {};

  for (const item of teamInfo) {
    const playerName = item?.fullName || item?.firstname + " " + item?.lastname;

    if (item?.hasOwnProperty("player_1_name")) {
      playerNameMappings["player_1"] = playerName;
    } else if (item?.hasOwnProperty("player_2_name")) {
      playerNameMappings["player_2"] = playerName;
    } else if (item?.hasOwnProperty("player_3_name")) {
      playerNameMappings["player_3"] = playerName;
    } else if (item?.hasOwnProperty("player_4_name")) {
      playerNameMappings["player_4"] = playerName;
    }
  }

  return playerNameMappings;
};

/**
 * Extracts player index and type from a player key
 * Parses keys like "player_1_full_shot" into { index: 0, type: "full_version" }
 *
 * @param {string} key - The player key (e.g., "player_1_full_shot", "player_2_half_shot")
 * @returns {{ index: number|null, type: string|null }} The player index (0-based) and type
 */
export const getPlayerIndexAndType = (key) => {
  if (typeof key !== "string" || !key.startsWith("player_")) {
    console.error("Invalid key format: Key must be a string starting with 'player_'. Received:", key);
    return { index: null, type: null };
  }

  const parts = key.split("_");

  if (parts.length < 3) {
    console.error("Invalid key format: Not enough parts separated by underscores. Received:", key);
    return { index: null, type: null };
  }

  const indexString = parts[1];
  const playerIndex = parseInt(indexString, 10);

  if (isNaN(playerIndex)) {
    console.error("Invalid key format: The part after 'player_' must be a number. Received:", indexString);
    return { index: null, type: null };
  }

  const typeParts = parts.slice(2);
  const playerType = typeParts.join("_");

  let type = null;
  if (playerType === "full_shot") type = PLAYER_GRAPHICS.FULL_IMAGE;
  else if (playerType === "half_shot") type = PLAYER_GRAPHICS.HALF_IMAGE;
  else type = playerType;

  return { index: playerIndex - 1, type };
};

/**
 * Extracts team index and type from a team key
 * Parses keys like "team_1_logo" into { index: 0, type: "team_logo" }
 *
 * @param {string} key - The team key (e.g., "team_1_logo", "team_2_shot")
 * @returns {{ index: number|null, type: string|null }} The team index (0-based) and type
 */
export const getTeamIndexAndType = (key) => {
  if (typeof key !== "string" || !key.startsWith("team_")) {
    console.error("Invalid key format: Key must be a string starting with 'team_'. Received:", key);
    return { index: null, type: null };
  }

  const parts = key.split("_");

  if (parts.length < 3) {
    console.error("Invalid key format: Not enough parts separated by underscores. Received:", key);
    return { index: null, type: null };
  }

  const indexString = parts[1];
  const teamIndex = parseInt(indexString, 10);

  if (isNaN(teamIndex)) {
    console.error("Invalid key format: The part after 'team_' must be a number. Received:", indexString);
    return { index: null, type: null };
  }

  const typeParts = parts.slice(2);
  const teamType = typeParts.join("_");

  let type = null;
  if (teamType === "logo") type = TEAM_GRAPHICS.LOGO;
  else if (teamType === "full_image") type = TEAM_GRAPHICS.FULL_IMAGE;
  else type = teamType;

  return { index: teamIndex - 1, type };
};

/**
 * Merges two objects with fallback logic
 * Values from obj2 override obj1, but only if they're not empty/null/undefined
 *
 * @param {Object} obj1 - The base object (fallback values)
 * @param {Object} obj2 - The override object (preferred values)
 * @returns {Object} Merged object with fallback logic applied
 */
export const mergeWithFallback = (obj1, obj2) => {
  const result = { ...obj1 };

  for (const key in obj2) {
    if (obj2.hasOwnProperty(key)) {
      const obj2Value = obj2[key];

      if (obj2Value !== "" && obj2Value !== null && obj2Value !== undefined) {
        result[key] = obj2Value;
      }
    }
  }

  return result;
};

/**
 * Generates a search term for capturing images based on player name mappings
 * Creates an OR-based search query from player last names
 *
 * @param {Object} playerNameMappings - Player name mappings (e.g., { player_1: { last_name: "Doe" } })
 * @returns {string} Search term string (e.g., "(Doe) OR (Smith)")
 */
export const generateCaptureSearchTerm = (playerNameMappings = {}) => {
  const searchTerm = Object.values(playerNameMappings)
    .map((name) => name?.last_name)
    .filter(Boolean)
    .map((name) => `(${name})`)
    .join(" OR ");

  return searchTerm || "";
};
